import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactSelect from "react-select";
import { usePagination, useSortBy, useTable } from "react-table";
import { SelectOptions } from "../../pages/SalesReturn/types";
import { ExcelIcon, PrintIcon } from "../../assets/images/svg/svg";
import { SearchKeyUpdates } from "../../utils/services";
import { exportToExcel, handlePrint } from "../../utils/utils";
import { Search, SearchKeysArray } from "../../api/get/types";
import { Loader } from "../Loader/Loader";
import { NoDataPage } from "../NoDataPage/NoDataPage";
import { FilterDate } from "../SalesList/SalesListPage";
import { Select } from "../Select";
import { useAuth } from "../../context/auth/auth";
import { UserInfoResponse } from "../../context/auth/types";
import "./CustomReactTable.scss";
import NepaliDatePicker from "../NepaliDatePicker/NepaliDatePicker";

export interface FilterConfig {
  type: "select" | "search" | "date" | "uptoDate" | "selectOptions";
  colMd?: number;
  label?: string;
  value?: any;
  onChange?: (value: any) => void;
  options?: SelectOptions[];
  searchKey?: string;
  placeholder?: string;
}

export interface TableHeaderContentGroupProps {
  totalEntries?: number;
  columns?: any;
  data?: any;
  currentPageNumber?: number;
  totalPages?: number;
  loading?: boolean;
  queryPageIndex?: number;
  setQueryPageIndex?: React.Dispatch<React.SetStateAction<number>>;
  queryPageSize?: number;
  setQueryPageSize?: React.Dispatch<React.SetStateAction<number>>;
  excelDownload?: boolean;
  showPagination?: boolean;
  showPageSize?: boolean;
  smallPagination?: boolean;
  onDoubleClick?: (row: any) => void;
  description?: string;
  filters?: FilterConfig[];
  showFooter?: boolean;
  searchTerm?: Search;
  setSearchTerm?: React.Dispatch<React.SetStateAction<Search>>;
  searchArray?: SearchKeysArray;
  date?: FilterDate;
  printData?: {
    title: string;
    date: string;
  };
}

function useInstance(instance: any) {
  const { allColumns } = instance;

  let rowSpanHeaders: any[] = [];

  allColumns.forEach((column: any, i: any) => {
    const { id, enableRowSpan } = column;

    if (enableRowSpan !== undefined) {
      rowSpanHeaders = [
        ...rowSpanHeaders,
        { id, topCellValue: null, topCellIndex: 0 },
      ];
    }
  });

  Object.assign(instance, { rowSpanHeaders });
}

export const Table: React.FC<TableHeaderContentGroupProps> = ({
  columns,
  data,
  currentPageNumber,
  totalPages,
  loading,
  totalEntries,
  queryPageIndex,
  setQueryPageIndex,
  queryPageSize,
  setQueryPageSize,
  excelDownload = false,
  showPagination = true,
  showPageSize = true,
  smallPagination = false,
  onDoubleClick,
  description,
  filters = [],
  showFooter = false,
  searchTerm,
  setSearchTerm,
  searchArray,
  date,
  printData,
}) => {
  const location = useLocation();
  const { businessUserInfo } = useAuth();

  const currentFiscalYear: any = localStorage.getItem("currentFiscalYear");
  const firstFiscalDate: any = localStorage.getItem("firstFiscalDate");
  const lastFiscalDate: any = localStorage.getItem("lastFiscalDate");

  const [inputValue, setInputValue] = useState({});
  const searchTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleSearchTerm = useCallback(
    (searchValue: string, key: string) => {
      setInputValue((prevState) => ({ ...prevState, [key]: searchValue }));

      // Clear previous timeout if exists
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      // Set new timeout
      searchTimerRef.current = setTimeout(() => {
        setSearchTerm?.((prevState) => ({ ...prevState, [key]: searchValue }));
      }, 300);
    },
    [setSearchTerm],
  );

  const useControlledState = (state: any) => {
    return useMemo(
      () => ({
        ...state,
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
      }),
      [state, queryPageIndex, queryPageSize],
    );
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    rowSpanHeaders,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      useControlledState,
      currentPageNumber,
      manualPagination: true,
      manualSortBy: true,
      autoResetPage: false,
      pageCount: totalPages,
    },
    (hooks) => {
      hooks.useInstance.push(useInstance);
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    if (totalPages && Number(queryPageIndex) + 1 > Number(totalPages)) {
      setQueryPageIndex?.(totalPages - 1);
    }
  }, [totalPages]);

  const renderFilter = (filter: FilterConfig, index: number) => {
    const colClass = `col-md-${filter.colMd || 3}`;

    switch (filter.type) {
      case "selectOptions":
        return (
          <div className={colClass} key={index}>
            <div className="form-group">
              <label className="d-block input-label">
                {filter.label || "Select"}
              </label>
              <ReactSelect
                isClearable={true}
                options={filter.options || []}
                value={filter.value}
                styles={{
                  control: (base) => ({
                    ...base,
                    fontSize: "12px",
                  }),
                  menu: (base) => ({
                    ...base,
                    fontSize: "12px",
                  }),
                }}
                onChange={(e) => {
                  filter.onChange?.(e);
                }}
              />
            </div>
          </div>
        );

      case "select":
        return (
          <div className={colClass} key={index}>
            <label className="input-label">{filter.label || "Select"}</label>
            <Select
              value={filter.value}
              onChange={(e) => {
                filter.onChange?.(Number(e.target.value));
              }}
            >
              {filter.options?.map((opt, idx) => (
                <option key={idx} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
        );

      case "search":
        return (
          <div className={colClass} key={index}>
            <div className="form-group mg-btm-16">
              <label className="input-label">{filter.label || "Search"}</label>
              <div className="search-wrapper">
                <div className="search-icon">
                  <img
                    alt="search"
                    src={require("../../assets/images/search.png")}
                  />
                </div>
                <input
                  value={filter.value || ""}
                  className="input-field"
                  placeholder={filter.placeholder || "Search here"}
                  onChange={(e) => {
                    filter.onChange?.(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
        );

      case "date":
        return (
          <div className={colClass} key={index}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group mg-btm-16">
                  <label htmlFor="date" className="input-label">
                    From Date
                  </label>
                  <NepaliDatePicker
                    defaultDate={firstFiscalDate}
                    onDateSelect={(value: any) => {
                      filter.onChange?.({ type: "from", value });
                    }}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="date" className="input-label">
                    To Date
                  </label>
                  <NepaliDatePicker
                    currentDate={currentFiscalYear === "Yes"}
                    defaultDate={
                      currentFiscalYear === "Yes" ? "" : lastFiscalDate
                    }
                    onDateSelect={(value: any) => {
                      filter.onChange?.({ type: "to", value });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "uptoDate":
        return (
          <div className={colClass} key={index}>
            <div className="form-group mg-btm-16">
              <label htmlFor="date" className="input-label">
                {filter.label || "Upto Date"}
              </label>
              <NepaliDatePicker
                currentDate={true}
                onDateSelect={(value: any) => {
                  filter.onChange?.(value);
                }}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="customer-react-table">
        <div className="col-md-12">
          <div className="row">
            {/* Render new flexible filters */}
            {filters.map((filter, index) => renderFilter(filter, index))}

            {searchArray && searchArray?.length > 0 && (
              <>
                {searchArray.map((val, index) => {
                  return (
                    <div className="col-md-3" key={index}>
                      <div className="form-group mg-btm-16">
                        <label className="input-label">
                          {SearchKeyUpdates(val)}
                        </label>
                        <div className="search-wrapper">
                          <div className="search-icon">
                            <img
                              alt="search"
                              src={require("../../assets/images/search.png")}
                            />
                          </div>
                          <input
                            value={
                              inputValue &&
                              inputValue[val as keyof typeof inputValue]
                            }
                            className="input-field"
                            placeholder="Search here"
                            name="user_name"
                            onChange={(e) => {
                              handleSearchTerm(e.target.value, val);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {excelDownload && (
              <div className="col-md-3 downloads">
                <div className="d-flex gap-3 float-right">
                  <button
                    className="d-flex btn btn-light my-4 "
                    onClick={() => {
                      exportToExcel(
                        "my-table",
                        columns,
                        location,
                        businessUserInfo as UserInfoResponse,
                        date as FilterDate,
                        printData,
                      );
                    }}
                  >
                    <span className="export-title"> Export</span>
                    <ExcelIcon />
                  </button>
                  <button
                    className="d-flex btn btn-light my-4 "
                    onClick={() => {
                      handlePrint(
                        columns,
                        businessUserInfo as UserInfoResponse,
                        date as FilterDate,
                        "my-table",
                        location,
                        printData,
                      );
                    }}
                  >
                    <span
                      style={{ marginRight: "10px" }}
                      className="export-title"
                    >
                      Print{" "}
                    </span>
                    <PrintIcon />
                  </button>
                </div>
              </div>
            )}

            <>
              {data && (
                <div className="col-md-12">
                  <div className="custom-react-table">
                    <div className="over-flow-table nested-column ">
                      <table {...getTableProps()} id="my-table">
                        <thead>
                          {columns.length > 1 && (
                            <>
                              {headerGroups.map((headerGroup, index) => (
                                <tr
                                  {...headerGroup.getHeaderGroupProps()}
                                  key={index}
                                >
                                  {headerGroup.headers.map((column, index) => (
                                    <th
                                      {...column.getHeaderProps()}
                                      key={index}
                                    >
                                      {column.render("Header")}
                                    </th>
                                  ))}
                                </tr>
                              ))}
                            </>
                          )}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                          {loading ? (
                            <Loader loaderType="Spinner" />
                          ) : (
                            <>
                              {rows.map((row, i) => {
                                prepareRow(row);

                                for (var j = 0; j < row.allCells.length; j++) {
                                  let cell = row.allCells[j];
                                  let rowSpanHeader = rowSpanHeaders.find(
                                    (x: any) => x.id === cell.column.id,
                                  );
                                  if (rowSpanHeader !== undefined) {
                                    if (
                                      i > 0 &&
                                      rows[i].values.invoice_no ===
                                      rows[i - 1].values.invoice_no
                                    ) {
                                      rows[rowSpanHeader.topCellIndex].allCells[
                                        j //@ts-ignore
                                      ].rowSpan++;
                                      //@ts-ignore
                                      cell.isRowSpanned = true;
                                    } else {
                                      //@ts-ignore
                                      cell.isRowSpanned = false;
                                      rowSpanHeader.topCellValue = cell.value;
                                      rowSpanHeader.topCellIndex = i;
                                      //@ts-ignore
                                      cell.rowSpan = 1;
                                    }
                                  }
                                }
                                return null;
                              })}
                              {rows.map((row) => {
                                return (
                                  <tr
                                    className="tablerow"
                                    onDoubleClick={() => {
                                      onDoubleClick?.(row.original);
                                    }}
                                    {...row.getRowProps()}
                                  >
                                    {row.cells.map((cell) => {
                                      //@ts-ignore
                                      if (cell.isRowSpanned) return null;
                                      else
                                        return (
                                          <td
                                            //@ts-ignore
                                            rowSpan={cell.rowSpan}
                                            {...cell.getCellProps()}
                                          >
                                            {cell.render("Cell")}
                                          </td>
                                        );
                                    })}
                                  </tr>
                                );
                              })}
                            </>
                          )}
                        </tbody>
                        {showFooter && !loading && data.length > 1 && (
                          <tfoot>
                            {footerGroups.map((group) => (
                              <tr {...group.getFooterGroupProps()}>
                                {group.headers.map((column) => (
                                  <td {...column.getFooterProps()}>
                                    {column.render("Footer")}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tfoot>
                        )}
                      </table>

                      {!loading &&
                        (data.length <= 0 || columns.length <= 0) && (
                          <NoDataPage description={description} />
                        )}
                    </div>
                    {showPagination && (
                      <div className="block-gap">
                        {showPageSize && (
                          <div className="show-wrapper">
                            <div className="entries">
                              <span className="content accent-2-charcoal-50">
                                Showing
                              </span>
                              <span className="show-drop">
                                <select
                                  className="select-tag"
                                  value={queryPageSize}
                                  onChange={(e) => {
                                    setQueryPageSize?.(Number(e.target.value));
                                  }}
                                >
                                  {[
                                    10, 20, 40, 80, 100, 200, 500, 1000, 2000,
                                    5000,
                                  ].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                      {pageSize}
                                    </option>
                                  ))}
                                </select>
                              </span>
                              <span className="content accent-2-charcoal-50">
                                entries
                              </span>
                            </div>
                            <div className="content entry-text">
                              Showing 1-{pageSize} (
                              {totalEntries ? totalEntries : "0"} entries)
                            </div>
                          </div>
                        )}
                        <div className="data-table">
                          <div className="data-buttons">
                            <button
                              className={`content light-secondary ${queryPageIndex === 0
                                  ? "remove-space-rt"
                                  : "prev"
                                } ${smallPagination && "small-button"}`}
                              onClick={() => {
                                const page = Number(queryPageIndex) - 1;
                                setQueryPageIndex?.(page);
                              }}
                              disabled={!canPreviousPage}
                            >
                              Prev
                            </button>
                            {queryPageIndex === 0 ? (
                              ""
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    const page = queryPageIndex
                                      ? Number(pageIndex) - 1
                                      : 0;
                                    setQueryPageIndex?.(page);
                                    // gotoPage(page)
                                  }}
                                  className={`content ${smallPagination && "small-button"
                                    } `}
                                >
                                  {queryPageIndex}
                                </button>
                              </>
                            )}
                            <button
                              className={`button-space active ${smallPagination && "small-button"
                                }`}
                            >
                              {Number(queryPageIndex) + 1}
                            </button>
                            {Number(queryPageIndex) + 1 < pageCount ? (
                              <button
                                onClick={() => {
                                  const page = Number(pageIndex) + 1;
                                  setQueryPageIndex?.(page);
                                  // gotoPage(page)
                                }}
                                className={`${smallPagination && "small-button"
                                  }`}
                              >
                                {Number(queryPageIndex) + 2}
                              </button>
                            ) : (
                              ""
                            )}
                            <button
                              className={`content light-secondary ${queryPageIndex === pageCount - 1
                                  ? "remove-space-rt"
                                  : "next"
                                } ${smallPagination && "small-button"}`}
                              onClick={() => {
                                const page = Number(pageIndex) + 1;
                                setQueryPageIndex?.(page);
                              }}
                              disabled={!canNextPage}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          </div>
        </div>
      </div>
    </>
  );
};
